// Story 6.11 (ADO-1165) — Admin User Management screen. Figma 572:3553.
//
// Realises the admin-facing user-management UI (FR6/FR7) using the Story 6.9
// design system, mirroring the Story 6.10 My Account screen. A "User
// administration" card holds a Create-User action + an (deferred) auth-audit-log
// link above a DS Table of users with per-row Edit / Assign role / Deactivate |
// Unblock actions, each gated behind DS Modals.
//
// Story 6.31 (ADO-1253) — reconciled to the Figma 2-state model (572:3553):
// Active | Locked, with Deactivate (red) / Unblock (green) affordances and a safe
// no-affordance fallback for an unknown status. The Story 6.16 (ADO-1195) wire
// value `Deactivated` (admin-deactivated) still arrives from the server but is
// PRESENTED as "Locked" here and restored via the same Unblock path — the old
// distinct "Reactivate"/"Deactivated" UI affordance is removed (Figma parity; the
// server contract + `terminateIfDeactivated` session handling are unchanged). The
// tenant depot list (OQ-6.11a) feeds the depot Select via `GET /admin/depots`;
// depot-required roles are validated inline before submit.
//
// Server remains authoritative (NFR18): `session.role` gates DISPLAY only; Story
// 6.12 re-checks every action (RolesGuard / @PersonaRestricted) and emits the
// Deactivate/Unblock audit events (to the log stream since Enabler E6-A). a11y via
// the DS components (NFR24–26); loading/disabled states keep the UI ≤3s (NFR6).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  Banner,
  Button,
  Card,
  FormField,
  Icon,
  Modal,
  Select,
  StatusBadge,
  Table,
  type TableColumn,
} from '../../design-system';
import type { SelectOption, StatusTone } from '../../design-system';
import { CatalogueLinkingHeader } from '../Products/CatalogueLinkingHeader';
import {
  ACCOUNT_TYPE_OPTIONS,
  ROLE_OPTIONS,
  accountTypeLabel,
  assignableRoleCodes,
  isAdminRole,
  isDepotRequiredRole,
  roleLabel,
} from '../../auth/rbac';
import {
  assignRole,
  createUser,
  deactivateUser,
  listDepots,
  listUsers,
  unblockUser,
  updateUser,
  type AdminUser,
  type AssignRoleBody,
  type CreateUserBody,
  type UpdateUserBody,
} from '../../api/users.client';
import styles from './AdminUsers.module.css';

// New-user welcome copy (final wording from Figma/PO — AC2).
const CREATE_USER_INFO =
  'A welcome email with password-set instructions is sent to the new user. ' +
  'New users default to the Store User role until changed.';

// New-user defaults (AC2; review 2026-07-06, re-keyed for ADR-020). Role defaults
// to StoreUser to match CREATE_USER_INFO; account type to LSN. This ensures the
// create POST never carries an empty role/accountType — the admin can change
// either before saving. Values mirror `@shoplink/tenant-context` (see auth/rbac.ts);
// note the mirror's ROLE_CODES now carries all 14 SL1 roles, of which only the six
// in R1_ASSIGNABLE_ROLE_CODES may be offered or defaulted to in R1.
const DEFAULT_NEW_ROLE = 'StoreUser';

/**
 * Options for a LOCKED role select — the acting admin may not change this user's
 * role, so the select shows exactly the current role, disabled.
 *
 * Code review 2026-07-31: filtering `ROLE_OPTIONS` alone renders an EMPTY select
 * whenever the current role is not one of the 14 canonical codes (a legacy value
 * served by an older API, or a role retired from the catalogue) — the field then
 * shows no options and no current value at all. Synthesise a fallback option in
 * that case, mirroring the depot lock immediately above, so the value stays
 * visible and is preserved on save.
 */
function lockedRoleOptions(role: string): SelectOption[] {
  const known = ROLE_OPTIONS.filter((o) => o.value === role);
  return known.length > 0 ? known : [{ value: role, label: roleLabel(role) }];
}
const DEFAULT_NEW_ACCOUNT_TYPE = 'LSN';

export interface AdminUsersProps {
  /**
   * Non-authoritative session values (from ID-token claims / `useSessionStore`).
   * `role` drives the client-side action gate (AC5). (`depotIds` was dropped by
   * ADO-1195: the depot Select now lists the TENANT's depots via
   * `GET /admin/depots`, not the acting admin's own claim depots.)
   */
  session?: { role?: string | null };
}

// Which dialog (if any) is open, and its target row. Only the active dialog is
// mounted, so each form's local state initialises fresh per open (no stale reset).
// `unblock` restores any blocked user (admin-deactivated OR brute-force locked)
// via the restore-access endpoint (Story 6.31 collapsed the former per-state
// Reactivate/Unblock split into the single Figma "Unblock").
type Dialog =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; user: AdminUser }
  | { kind: 'assign-role'; user: AdminUser }
  | { kind: 'deactivate'; user: AdminUser }
  | { kind: 'unblock'; user: AdminUser };

// ── Status rendering — 2-state display (Story 6.31 / ADO-1253) ────────────────
// Figma 572:3553 models only Active + Locked (Deactivate / Unblock). The server
// still emits the Story 6.16 `Deactivated` value (admin-deactivated — distinct at
// the wire + session level, e.g. `terminateIfDeactivated`), so the ADMIN TABLE
// collapses it into the "Locked" presentation: any blocked user shows a "Locked"
// badge + a single green "Unblock" restore (Deactivated and Locked call the same
// restore-access endpoint). An unknown/future/hostile status still renders
// neutrally with NO state-mutating affordance (ADO-1195 safety) — the label text
// carries the meaning, tone is never the only signal.
type StatusAction = 'deactivate' | 'unblock' | 'none';
interface StatusView {
  label: string;
  tone: StatusTone;
  action: StatusAction;
}

function statusView(status: string): StatusView {
  if (status === 'Active') return { label: 'Active', tone: 'success', action: 'deactivate' };
  // `Deactivated` (admin) and `Locked` (brute-force) both present as "Locked" with
  // an Unblock restore — the Figma 2-state model.
  if (status === 'Deactivated' || status === 'Locked')
    return { label: 'Locked', tone: 'warning', action: 'unblock' };
  // Unknown/hostile — neutral badge, no destructive/mutating action (label shown).
  return { label: status, tone: 'neutral', action: 'none' };
}

export function AdminUsers({ session }: AdminUsersProps) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // A post-mutation refresh that fails is NOT a mutation failure — surface it
  // as a non-blocking "list may be stale" warning instead of the modal error.
  const [refreshError, setRefreshError] = useState(false);
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });

  // Guard setState in `reload` against the component unmounting mid-refresh
  // (e.g. the route gate redirects while a post-mutation reload is in flight).
  // Reset to `true` in the setup body (not just via the initial `useRef(true)`):
  // under <StrictMode> React runs mount→cleanup→mount in dev, so without the
  // setup-side reset the ref would stay `false` after the double-invoke and
  // every post-mutation `reload()` would early-return (stale table in dev).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Client-side half of AC5: hiding actions the admin's role couldn't perform.
  // Non-authoritative — the server re-checks (NFR18). The route already blocks
  // non-admins; this keeps the guard explicit at the component boundary too.
  const canManage = isAdminRole(session?.role);

  // ADO-1195 (OQ-6.11a): the depot Select lists the TENANT's depots from
  // `GET /admin/depots` (retires the 6.11 D-4 claim-`depotIds` degradation).
  // The Select still degrades to a disabled placeholder on failure, but we track
  // WHY separately (review): a genuine fetch error is a distinct, RECOVERABLE
  // state from a tenant that legitimately has no depots — the depot-required
  // validation message differs so a transient error doesn't read as the terminal
  // "no depots exist, pick another role".
  // `null` while GET /admin/depots is in flight (ADO-1195 review): the load
  // window is a THIRD state, distinct from a load error and a genuinely
  // depot-less tenant. Before this story the options came synchronously from
  // the session claims, so the in-flight race is new — hold back the terminal
  // "no depots" placeholder/validation copy until the fetch settles.
  const [depots, setDepots] = useState<{ value: string; label: string }[] | null>(null);
  const [depotLoadError, setDepotLoadError] = useState(false);
  useEffect(() => {
    let alive = true;
    listDepots()
      .then((list) => {
        if (!alive) return;
        // Dedupe by id (review): a malformed mapping with a duplicate id would
        // otherwise yield duplicate Select options and duplicate React keys.
        const seen = new Set<string>();
        const options: { value: string; label: string }[] = [];
        for (const d of list) {
          if (seen.has(d.id)) continue;
          seen.add(d.id);
          options.push({ value: d.id, label: d.key });
        }
        setDepots(options);
        setDepotLoadError(false);
      })
      .catch(() => {
        // Degrade to an empty option list, but flag it as an ERROR (distinct from
        // an empty tenant) — the users table itself must not fail because the
        // depot read did.
        if (!alive) return;
        setDepots([]);
        setDepotLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, []);
  const depotsLoading = depots === null;

  // AC5: the acting admin's assignable-role set (a StoreManagerAdmin can't confer
  // SystemAdministrator, which the API would reject). Non-authoritative — the
  // server re-checks every write.
  const assignableRoles = assignableRoleCodes(session?.role);

  // Never throws: a mutation succeeded before this runs, so a failed refresh
  // must not propagate into the modal's catch and be misreported as a failed
  // create/edit/deactivate (which would invite a destructive retry). Instead we
  // keep the (now stale) table and raise a non-blocking refresh warning.
  const reload = useCallback(async () => {
    try {
      const next = await listUsers();
      if (!mountedRef.current) return;
      setUsers(next);
      setRefreshError(false);
    } catch {
      if (mountedRef.current) setRefreshError(true);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listUsers()
      .then((next) => {
        if (!alive) return;
        setUsers(next);
        setLoadError(false);
      })
      .catch(() => alive && setLoadError(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const close = useCallback(() => setDialog({ kind: 'none' }), []);

  const columns: TableColumn<AdminUser>[] = [
    {
      key: 'user',
      header: 'User',
      render: (u) => (
        <span className={styles.userCell}>
          <span className={styles.userName}>{u.fullName}</span>
          <span className={styles.userEmail}>{u.email}</span>
        </span>
      ),
    },
    { key: 'role', header: 'Role', render: (u) => roleLabel(u.role) },
    { key: 'depot', header: 'Depot', render: (u) => u.depotId ?? '—' },
    { key: 'accountType', header: 'Account type', render: (u) => accountTypeLabel(u.accountType) },
    {
      key: 'status',
      header: 'Status',
      // Story 6.31 (ADO-1253): 2-state display (Active / Locked); an unknown value
      // renders neutral (label shown, no destructive affordance).
      render: (u) => {
        const s = statusView(u.status);
        return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (u) => {
        if (!canManage) return null;
        const { action } = statusView(u.status);
        return (
          <span className={styles.rowActions}>
            <Button
              variant="tertiary"
              leadingIcon={<Icon name="edit" size={16} />}
              onClick={() => setDialog({ kind: 'edit', user: u })}
            >
              Edit
            </Button>
            <Button variant="tertiary" onClick={() => setDialog({ kind: 'assign-role', user: u })}>
              Assign role
            </Button>
            {/* Story 6.31 (ADO-1253) — Figma 2-state actions: Active→Deactivate
                (red); any blocked user (Deactivated or Locked)→Unblock (primary
                GREEN restore per Figma "primary green Unblock"); unknown→none. The
                Deactivate / Unblock paths hit the deactivate / restore-access
                endpoints (Unblock restores both admin-deactivated and locked). */}
            {action === 'deactivate' ? (
              <Button
                variant="destructive"
                leadingIcon={<Icon name="trash" size={16} />}
                onClick={() => setDialog({ kind: 'deactivate', user: u })}
              >
                Deactivate
              </Button>
            ) : action === 'unblock' ? (
              <Button variant="primary" onClick={() => setDialog({ kind: 'unblock', user: u })}>
                Unblock
              </Button>
            ) : null}
          </span>
        );
      },
    },
  ];

  return (
    <div className={styles.page}>
      {/* The full catalogue-linking header (Story 6.31, OQ-6.31a; ADO-1779), shared
          with every signed-in page. Its depot switcher is the session's; the
          `depots` Select below is the tenant list from GET /admin/depots. */}
      <CatalogueLinkingHeader showManagerDashboard={canManage} />

      <main className={styles.main}>
        <div className={styles.pageHead}>
          <h1 className={styles.pageTitle}>Admin user management</h1>
          <p className={styles.pageSubtitle}>
            Create and manage the users, roles and depots for your organisation.
          </p>
        </div>

        {loadError ? (
          <Banner type="error" title="Couldn't load users">
            Please reload the page and try again.
          </Banner>
        ) : (
          <div className={styles.stack}>
            {refreshError ? (
              <Banner type="warning" title="This list may be out of date">
                Your change was saved, but the users list couldn't refresh. Please reload the page
                to see the latest.
              </Banner>
            ) : null}

            {/* Card 1 — User administration (action card; Figma 572:3553). The table
                lives in its own "Users" card below (two-card anatomy). */}
            <Card
              title="User administration"
              subtitle="Add a new store or depot account, or manage existing users in the table below."
              headerAction={
                canManage ? (
                  <Button
                    leadingIcon={<Icon name="userPlus" size={16} />}
                    onClick={() => setDialog({ kind: 'create' })}
                  >
                    Create User
                  </Button>
                ) : undefined
              }
            >
              {/* Story 6.22 (ADO-1201): the auth-audit-log view has a live
                  destination (/admin/audit-log, D-3 resolved) — a real navigable
                  link, never the old disabled "coming soon" stub. */}
              <div className={styles.related}>
                <span className={styles.relatedLabel}>Related</span>
                <Link className={styles.auditLogLink} to="/admin/audit-log">
                  <Icon name="history" size={16} />
                  Auth audit log
                </Link>
                {/* Story 14.4 (ADO-1090): the import-monitoring dashboard now has
                    a navigable destination at /admin/import-monitoring. */}
                <Link className={styles.auditLogLink} to="/admin/import-monitoring">
                  <Icon name="chartBar" size={16} />
                  Import monitoring
                </Link>
              </div>
            </Card>

            {/* Card 2 — Users table. "Role assignment enabled" indicator is truthful
                (shown only when the acting admin canManage). */}
            <Card
              title="Users"
              headerAction={
                canManage ? (
                  <span className={styles.roleAssign}>
                    <span className={styles.roleAssignDot} aria-hidden />
                    Role assignment enabled
                  </span>
                ) : undefined
              }
            >
              {loading ? (
                <p role="status" className={styles.loading}>
                  Loading users…
                </p>
              ) : (
                <Table
                  caption="Users"
                  hideCaption
                  columns={columns}
                  rows={users}
                  rowKey={(u) => u.id}
                  emptyMessage="No users yet. Use “Create User” to add the first one."
                />
              )}
            </Card>
          </div>
        )}

        {dialog.kind === 'create' ? (
          <UserFormModal
            mode="create"
            depotOptions={depots ?? []}
            depotsLoading={depotsLoading}
            depotLoadError={depotLoadError}
            assignableRoles={assignableRoles}
            onClose={close}
            onSubmit={async (body) => {
              await createUser(body);
              await reload();
            }}
          />
        ) : null}

        {dialog.kind === 'edit' ? (
          <UserFormModal
            mode="edit"
            user={dialog.user}
            depotOptions={depots ?? []}
            depotsLoading={depotsLoading}
            depotLoadError={depotLoadError}
            assignableRoles={assignableRoles}
            onClose={close}
            onSubmit={async (body) => {
              await updateUser(dialog.user.id, body);
              await reload();
            }}
          />
        ) : null}

        {dialog.kind === 'assign-role' ? (
          <AssignRoleModal
            user={dialog.user}
            assignableRoles={assignableRoles}
            onClose={close}
            onSubmit={async (body) => {
              await assignRole(dialog.user.id, body);
              await reload();
            }}
          />
        ) : null}

        {dialog.kind === 'deactivate' ? (
          <ConfirmModal
            title="Deactivate user?"
            description={`This deactivates ${dialog.user.fullName}. This action is recorded in the audit trail.`}
            confirmLabel="Deactivate user"
            confirmVariant="destructive"
            body="The user will be signed out and can no longer access ShopLink until unblocked."
            onClose={close}
            onConfirm={async () => {
              await deactivateUser(dialog.user.id);
              await reload();
            }}
          />
        ) : null}

        {dialog.kind === 'unblock' ? (
          <ConfirmModal
            title="Unblock account?"
            // Story 6.31 (ADO-1253): this single Unblock path now restores BOTH a
            // brute-force-locked account AND an admin-deactivated one (both present
            // as "Locked"). The copy therefore avoids asserting a failed-sign-in
            // counter reset that doesn't apply to an admin-deactivated user — "any
            // failed sign-in lock is cleared" is accurate for both.
            description={`This restores access for ${dialog.user.fullName}. This action is recorded in the audit trail.`}
            confirmLabel="Unblock account"
            confirmVariant="primary"
            body="The user can sign in and access ShopLink again. Any failed sign-in lock is cleared and their multi-factor authentication (MFA) enrolment is unchanged."
            onClose={close}
            onConfirm={async () => {
              await unblockUser(dialog.user.id);
              await reload();
            }}
          />
        ) : null}
      </main>
    </div>
  );
}

// ── Create / Edit user form (AC2 / AC3) ──────────────────────────────────────

interface UserFormModalProps {
  mode: 'create' | 'edit';
  /** Prefill for edit mode. */
  user?: AdminUser;
  depotOptions: { value: string; label: string }[];
  /** True while GET /admin/depots is still in flight (distinct from empty/error). */
  depotsLoading: boolean;
  /** True when the depot list FAILED to load (vs. a genuinely depot-less tenant). */
  depotLoadError: boolean;
  /** Role codes the acting admin may assign (AC5). */
  assignableRoles: readonly string[];
  onClose: () => void;
  onSubmit: (body: CreateUserBody & UpdateUserBody) => Promise<void>;
}

function UserFormModal({
  mode,
  user,
  depotOptions,
  depotsLoading,
  depotLoadError,
  assignableRoles,
  onClose,
  onSubmit,
}: UserFormModalProps) {
  const isEdit = mode === 'edit';
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  // Create mode prefills defaults so the POST never carries an empty role/type
  // (review 2026-07-06); edit mode prefills from the row.
  const [role, setRole] = useState(user?.role ?? DEFAULT_NEW_ROLE);
  const [depotId, setDepotId] = useState(user?.depotId ?? '');
  const [accountType, setAccountType] = useState(user?.accountType ?? DEFAULT_NEW_ACCOUNT_TYPE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  // ADO-1195 (OQ-6.11a): inline depot-required validation — a depot-required
  // role never submits `depotId: null` (the 6.12 server gate would 422 it;
  // blocking client-side keeps the modal open with an actionable message).
  const [depotError, setDepotError] = useState(false);
  // Re-entrancy guard: `setSubmitting(true)` is async, so Enter-in-field (form
  // onSubmit) racing the footer button — or a rapid double Enter — could fire
  // `submit()` twice and create a duplicate (non-idempotent). The ref blocks the
  // second call synchronously, before React re-renders with the disabled button.
  const submittingRef = useRef(false);
  // The footer submit button sits outside <form> (Modal renders it in the footer
  // slot), so clicking it bypasses native `required` validation — that only fires
  // on the Enter/form-submit path. `reportValidity()` in `submit()` closes the gap
  // for both required fields (Full name, Email) on both paths (review 2026-07-06).
  const formRef = useRef<HTMLFormElement>(null);

  // Review 2026-07-06 (kept under ADO-1195): when editing a user whose depot is
  // outside the option list (now the TENANT list from GET /admin/depots — e.g. a
  // stale/foreign depot id, or the depot fetch degraded to []), surface the
  // current depot as a read-only (disabled) option so it is visible and
  // preserved rather than silently overwritten on first interaction.
  const depotInOptions = depotOptions.some((o) => o.value === depotId);
  const lockDepot = isEdit && depotId !== '' && !depotInOptions;
  const resolvedDepotOptions = lockDepot ? [{ value: depotId, label: depotId }] : depotOptions;

  // Same guard for role (AC5 / D-4 rationale): when editing a user whose current
  // role is outside the acting admin's assignable set (e.g. a StoreManagerAdmin
  // editing a SystemAdministrator), lock the select to the current role so it is
  // preserved and visible rather than silently downgraded, and no higher role can
  // be conferred.
  const roleAssignable = assignableRoles.includes(role);
  const lockRole = isEdit && !roleAssignable;
  const resolvedRoleOptions = lockRole
    ? lockedRoleOptions(role)
    : ROLE_OPTIONS.filter((o) => assignableRoles.includes(o.value));

  // Shared submit path so BOTH the footer button (onClick — the footer sits
  // outside the <form>, so the `form=` attribute association is unreliable in the
  // test DOM) and Enter-in-a-field (form onSubmit) go through one place.
  const submit = useCallback(async () => {
    if (submittingRef.current) return;
    // ADO-1195 (OQ-6.11a): a depot-required role must carry a depot. Checked
    // BEFORE the native-validation gate so the visible inline message (not a
    // browser bubble) is what the admin sees; the 6.12 server 422 backs it up.
    if (isDepotRequiredRole(role) && depotId === '') {
      setDepotError(true);
      return;
    }
    // Gate both submit paths on native validation before locking the guard, so a
    // rejected submit (empty required field) leaves the form re-submittable.
    if (!formRef.current?.reportValidity()) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(false);
    try {
      await onSubmit({
        fullName: fullName.trim(),
        email: email.trim(),
        role,
        depotId: depotId === '' ? null : depotId,
        accountType,
      });
      onClose();
    } catch {
      setError(true); // keep the modal open on error (AC2/AC3)
      setSubmitting(false);
      submittingRef.current = false; // allow a retry
    }
  }, [fullName, email, role, depotId, accountType, onSubmit, onClose]);

  // Block Escape / backdrop / close-X while a submit is in flight so the form
  // can't be unmounted mid-request (footer buttons are already disabled).
  const requestClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);

  return (
    <Modal
      open
      onClose={requestClose}
      title={isEdit ? 'Edit user' : 'Create user'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={submitting}>
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create user'}
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {!isEdit ? <Banner type="info">{CREATE_USER_INFO}</Banner> : null}
        {error ? (
          <Banner type="error">
            We couldn't {isEdit ? 'save this user' : 'create the user'}. Please try again.
          </Banner>
        ) : null}
        <FormField
          label="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="name"
          required
        />
        <FormField
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <Select
          label="Role"
          options={resolvedRoleOptions}
          value={role}
          onChange={(next) => {
            setRole(next);
            setDepotError(false); // rule may no longer apply — revalidated on submit
          }}
          disabled={lockRole}
        />
        <Select
          label="Depot"
          options={resolvedDepotOptions}
          value={depotId}
          onChange={(next) => {
            setDepotId(next);
            setDepotError(false);
          }}
          disabled={lockDepot}
          placeholder={
            depotsLoading
              ? 'Loading depots…'
              : depotOptions.length === 0
                ? 'No depots available'
                : 'Select a depot'
          }
        />
        {depotError ? (
          <p role="alert" className={styles.fieldError}>
            {roleLabel(role)} requires a depot.{' '}
            {depotsLoading
              ? 'Depots are still loading — wait a moment and try again.'
              : depotOptions.length > 0
                ? 'Select a depot to continue.'
                : depotLoadError
                  ? "Depots couldn't be loaded — reload the page and try again."
                  : 'No depots are available for this organisation — choose a different role or contact support.'}
          </p>
        ) : null}
        <Select
          label="Account type"
          options={ACCOUNT_TYPE_OPTIONS}
          value={accountType}
          onChange={setAccountType}
        />
      </form>
    </Modal>
  );
}

// ── Assign role (role + account type only, AC3) ──────────────────────────────

function AssignRoleModal({
  user,
  assignableRoles,
  onClose,
  onSubmit,
}: {
  user: AdminUser;
  assignableRoles: readonly string[];
  onClose: () => void;
  onSubmit: (body: AssignRoleBody) => Promise<void>;
}) {
  const [role, setRole] = useState(user.role);
  const [accountType, setAccountType] = useState(user.accountType);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  // ADO-1195 (OQ-6.11a): this form carries no depot field, so a depot-required
  // role is validated against the TARGET's existing depot — matching the 6.12
  // server gate (422 against the registry's depot_ids).
  const [depotError, setDepotError] = useState(false);
  const submittingRef = useRef(false); // re-entrancy guard (see UserFormModal)

  // AC5 (see UserFormModal): lock the select to the user's current role when it
  // sits outside the acting admin's assignable set, preserving it rather than
  // exposing a re-role the API would reject or silently downgrading it.
  const lockRole = !assignableRoles.includes(role);
  const resolvedRoleOptions = lockRole
    ? lockedRoleOptions(role)
    : ROLE_OPTIONS.filter((o) => assignableRoles.includes(o.value));

  const submit = useCallback(async () => {
    if (submittingRef.current) return;
    // ADO-1195 (OQ-6.11a): the target needs a depot before a depot-required
    // role can be assigned — direct the admin to Edit user first.
    if (isDepotRequiredRole(role) && user.depotId === null) {
      setDepotError(true);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(false);
    try {
      await onSubmit({ role, accountType });
      onClose();
    } catch {
      setError(true);
      setSubmitting(false);
      submittingRef.current = false;
    }
  }, [role, accountType, user.depotId, onSubmit, onClose]);

  // Block dismiss while a submit is in flight (see UserFormModal).
  const requestClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);

  return (
    <Modal
      open
      onClose={requestClose}
      // OQ-6.31c RESOLVED (2026-07-22): the Figma design was corrected — the
      // assign-role modal is titled "Assign role" (the earlier "Edit User" was a
      // design bug). Keeping the accurate title also gives this dialog an
      // accessible name distinct from the "Edit user" dialog.
      title="Assign role"
      description={`Update the role and account type for ${user.fullName}.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {error ? (
          <Banner type="error">We couldn't update the role. Please try again.</Banner>
        ) : null}
        <Select
          label="Role"
          options={resolvedRoleOptions}
          value={role}
          onChange={(next) => {
            setRole(next);
            setDepotError(false);
          }}
          disabled={lockRole}
        />
        {depotError ? (
          <p role="alert" className={styles.fieldError}>
            {roleLabel(role)} requires a depot. Assign a depot via “Edit” first, then change the
            role.
          </p>
        ) : null}
        <Select
          label="Account type"
          options={ACCOUNT_TYPE_OPTIONS}
          value={accountType}
          onChange={setAccountType}
        />
      </form>
    </Modal>
  );
}

// ── Destructive/confirm dialog (Deactivate / Unblock, AC4) ───────────────────

function ConfirmModal({
  title,
  description,
  body,
  confirmLabel,
  confirmVariant,
  onClose,
  onConfirm,
}: {
  title: string;
  description: string;
  body: string;
  confirmLabel: string;
  confirmVariant: 'primary' | 'destructive';
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const submittingRef = useRef(false); // re-entrancy guard (see UserFormModal)

  const confirm = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(false);
    try {
      await onConfirm();
      onClose();
    } catch {
      setError(true);
      setSubmitting(false);
      submittingRef.current = false;
    }
  }, [onConfirm, onClose]);

  // Block dismiss while a confirm is in flight (see UserFormModal).
  const requestClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);

  return (
    <Modal
      open
      onClose={requestClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant={confirmVariant} onClick={confirm} disabled={submitting}>
            {submitting ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    >
      {/* Keep the safety notice visible on retry — an error must ADD a banner,
          not REPLACE the "recorded in the audit log" / MFA + reset-counter text
          the admin needs to see to confirm what they're re-attempting (AC4). */}
      {error ? <Banner type="error">Something went wrong. Please try again.</Banner> : null}
      <p className={styles.confirmBody}>{body}</p>
    </Modal>
  );
}
